import { Menu } from 'primereact/menu';
import { Toolbar } from 'primereact/toolbar';
import { ViewMode } from "../libs/gantt-task-react"
import { Dropdown } from 'primereact/dropdown';
import { Button } from "primereact/button";
import { useCreateTaskMutation, useBulkUpdateTasksMutation, useUndoLastChangeMutation } from "../services/tasksApi";
import ProjectDropdown from './ProjectDropdown';
import { useToast } from "../hooks/useToast.ts";
import { useGetUserQuery } from "../services/usersApi";
import UserDropdown from './UserDropdown';
import BranchDropdown from "./BranchDropdown";
import { FloatLabel } from 'primereact/floatlabel';
import { SelectButton } from 'primereact/selectbutton';
import { useEffect, useState, useCallback, useRef } from 'react';
import { Sidebar } from "primereact/sidebar";
import { useLazyGetToastsQuery } from "../services/toastsApi";
import { Card } from "primereact/card";
import { useChannelQuery } from "../services/websocketApi.ts";
import { useGetViewsQuery } from '../services/viewsApi.js';
import { useDeleteViewMutation } from '../services/viewsApi.js';
import { SplitButton } from 'primereact/splitbutton';
import { Slider } from 'primereact/slider';
import { Dialog } from 'primereact/dialog';
import { Checkbox } from 'primereact/checkbox';
export default function TopMenu({ onViewModeChange, view, onChangeProject, project, taskMenu = false, selectedUser, onChangeSelectedUser, selectedBranch, onChangeSelectedBranch, tasks = [], users = [], institutions = [], onChangeEditorMode, onExpandAll, onCollapseAll, onExpandAllChains, onCollapseAllChains, rowHeight, onRowHeightChange, columnWidth, onColumnWidthChange, barFill, onBarFillChange, sortBy, onSortChange, columnVisibility, onColumnVisibilityChange, selectedProjectStatus, onChangeProjectStatus, showProjects = true, onShowProjectsChange, onCreateProjectRequest }) {
    const TOASTS_PAGE_SIZE = 20;
    const [createTask] = useCreateTaskMutation();
    const { data: views, refetch: refetchViews } = useGetViewsQuery();
    const toast = useToast();
    const { data: user } = useGetUserQuery();
    const [deleteView] = useDeleteViewMutation();
    const [undoLastChange] = useUndoLastChangeMutation();
    const [isUndoing, setIsUndoing] = useState(false);
    const options = ['Tak', 'Nie'];
    const [value, setValue] = useState(options[0]);
    const [sidebarVisible, setSidebarVisible] = useState(false);
    const { data: toastsEvents } = useChannelQuery('toasts');
    const [triggerGetToasts] = useLazyGetToastsQuery();
    const [bulkUpdateTasks] = useBulkUpdateTasksMutation();
    const menuRef = useRef(null);
    const isLoadingToastsRef = useRef(false);
    const lastToastEventKeyRef = useRef(null);
    const toastFnRef = useRef(toast);
    const [settingsDialogVisible, setSettingsDialogVisible] = useState(false);
    const [tempRowHeight, setTempRowHeight] = useState(rowHeight || 50);
    const [tempColumnWidth, setTempColumnWidth] = useState(columnWidth || 65);
    const [tempBarFill, setTempBarFill] = useState(barFill || 80);
    const [toastHistory, setToastHistory] = useState([]);
    const [toastHasMore, setToastHasMore] = useState(true);
    const [toastSkip, setToastSkip] = useState(0);
    const [isLoadingToasts, setIsLoadingToasts] = useState(false);
    const [tempColumnVisibility, setTempColumnVisibility] = useState(columnVisibility || {
        coordinator: true,
        executor: true,
        branch: true,
        name: true,
        start: true,
        end: true,
        progress: true
    });

    useEffect(() => {
        toastFnRef.current = toast;
    }, [toast]);

    const loadToastsPage = useCallback(async ({ skip = 0, append = false } = {}) => {
        if (isLoadingToastsRef.current) {
            return;
        }

        isLoadingToastsRef.current = true;
        setIsLoadingToasts(true);
        try {
            const response = await triggerGetToasts({ limit: TOASTS_PAGE_SIZE, skip }).unwrap();
            const items = response?.items || [];

            setToastHistory((prev) => {
                if (!append) {
                    return [...items].sort((a, b) => {
                        const timeDiff = new Date(b.date).getTime() - new Date(a.date).getTime();
                        if (timeDiff !== 0) return timeDiff;
                        return String(b._id || '').localeCompare(String(a._id || ''));
                    });
                }

                const mergedById = new Map();
                [...prev, ...items].forEach((item) => {
                    if (item?._id) {
                        mergedById.set(item._id, item);
                    }
                });

                return Array.from(mergedById.values()).sort((a, b) => {
                    const timeDiff = new Date(b.date).getTime() - new Date(a.date).getTime();
                    if (timeDiff !== 0) return timeDiff;
                    return String(b._id || '').localeCompare(String(a._id || ''));
                });
            });

            setToastHasMore(Boolean(response?.hasMore));
            setToastSkip(skip + items.length);
        } catch (err) {
            toastFnRef.current(err?.data?.message || 'Nie udało się pobrać historii', 'error');
        } finally {
            isLoadingToastsRef.current = false;
            setIsLoadingToasts(false);
        }
    }, [TOASTS_PAGE_SIZE, triggerGetToasts]);

    // Update temp values when props change
    useEffect(() => {
        setTempRowHeight(rowHeight || 50);
        setTempColumnWidth(columnWidth || 65);
        setTempBarFill(barFill || 80);
        if (columnVisibility) setTempColumnVisibility(columnVisibility);
    }, [rowHeight, columnWidth, barFill, columnVisibility]);

    const handleSaveSettings = () => {
        // Validate values before saving
        const validatedRowHeight = !isNaN(tempRowHeight) && tempRowHeight > 0 ? tempRowHeight : 50;
        const validatedColumnWidth = !isNaN(tempColumnWidth) && tempColumnWidth > 0 ? tempColumnWidth : 65;
        const validatedBarFill = !isNaN(tempBarFill) && tempBarFill > 0 && tempBarFill <= 100 ? tempBarFill : 80;
        
        onRowHeightChange?.(validatedRowHeight);
        onColumnWidthChange?.(validatedColumnWidth);
        onBarFillChange?.(validatedBarFill);
        onColumnVisibilityChange?.(tempColumnVisibility);
        setSettingsDialogVisible(false);
        toast('Ustawienia zapisane', 'success');
    };

    const handleUndo = useCallback(async () => {
        if (isUndoing) {
            return;
        }

        setIsUndoing(true);
        try {
            const result = await undoLastChange().unwrap();
            toast(`Cofnięto: ${result.action}`, 'success');
        } catch (err) {
            toast(err?.data?.message || 'Nie udało się cofnąć operacji', 'error');
        } finally {
            setIsUndoing(false);
        }
    }, [isUndoing, toast, undoLastChange]);

    useEffect(() => {
        const onGlobalKeyDown = (event) => {
            const isUndoShortcut =
                (event.ctrlKey || event.metaKey) &&
                !event.shiftKey &&
                (event.key?.toLowerCase() === 'z' || event.code === 'KeyZ');
            if (!isUndoShortcut) {
                return;
            }

            const target = event.target;
            const isEditableTarget = target?.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target?.tagName);
            if (isEditableTarget) {
                return;
            }

            if (user?.type === "admin" || user?.type === "manager") {
                event.preventDefault();
                handleUndo();
            }
        };

        window.addEventListener('keydown', onGlobalKeyDown, true);
        return () => window.removeEventListener('keydown', onGlobalKeyDown, true);
    }, [handleUndo, user?.type]);

    const expandCollapseMenuItems = [
        {
            label: 'Rozwiń wszystkie projekty',
            icon: 'pi pi-angle-down',
            command: () => {
                onExpandAll?.();
            }
        },
        {
            label: 'Zwiń wszystkie projekty',
            icon: 'pi pi-angle-up',
            command: () => {
                onCollapseAll?.();
            }
        },
        {
            separator: true
        },
        {
            label: 'Rozwiń wszystkie zadania zależne',
            icon: 'pi pi-sitemap',
            command: () => {
                onExpandAllChains?.();
            }
        },
        {
            label: 'Zwiń wszystkie zadania zależne',
            icon: 'pi pi-sitemap',
            command: () => {
                onCollapseAllChains?.();
            }
        }
    ];
    useEffect(() => {
        if (sidebarVisible) {
            loadToastsPage({ skip: 0, append: false });
        }
    }, [sidebarVisible, loadToastsPage]);

    useEffect(() => {
        const messages = toastsEvents?.messages || [];
        const latest = messages[messages.length - 1];
        if (!latest) return;

        const latestKey = latest._id || `${latest.id || ''}-${latest.author || ''}-${latest.message || ''}`;
        if (!latestKey || lastToastEventKeyRef.current === latestKey) return;
        lastToastEventKeyRef.current = latestKey;

        if (!sidebarVisible) return;

        loadToastsPage({ skip: 0, append: false });
    }, [toastsEvents?.messages, loadToastsPage, sidebarVisible]);

    const handleToastSidebarScroll = useCallback((event) => {
        if (!toastHasMore || isLoadingToasts) {
            return;
        }

        const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
        if (scrollHeight - scrollTop - clientHeight < 48) {
            loadToastsPage({ skip: toastSkip, append: true });
        }
    }, [isLoadingToasts, loadToastsPage, toastHasMore, toastSkip]);

    const handleDeleteView = (id, name) => {

        deleteView(views.filter((v) => v._id === id)[0]).unwrap().then(() => {
            refetchViews();
            toast("Szablon " + name + " został usunięty", "error");
        })

    };


    const addView = async (id) => {

        try {
            const toAddTasks = JSON.parse(views.filter((v) => v._id === id)[0].items)

            const dependencies = toAddTasks.map(task => task.dependencies[0])

            let newIds = [];
            newIds[0] = (await createTask(
                {
                    ...toAddTasks.filter((t) => t.type === "project")[0],
                    start: new Date(new Date().getTime() + toAddTasks.filter((t) => t.type === "project")[0].start),
                    end: new Date(new Date().getTime() + toAddTasks.filter((t) => t.type === "project")[0].end), displayOrder: getNextProjectDisplayOrder()
                })).data.id;

            let idCount = newIds.length;


            toAddTasks.filter((t) => t.type === "task").forEach(async (task) => {

                const newObj = { ...task }
                newObj.dependencies = []

                newIds[idCount] = await createTask({ ...newObj, project: newIds[0], start: new Date(new Date().getTime() + task.start), end: new Date(new Date().getTime() + task.end), users: selectedUser || [] }).unwrap().then((x) => {


                    return x.id
                }).finally(idCount++);
                if (idCount === toAddTasks.length) {

                    let iteration = 0;
                    dependencies.forEach((dependency) => {

                        if (dependency) {
                            bulkUpdateTasks([{ ...task, dependencies: [newIds[dependency]], project: newIds[0], _id: newIds[iteration], start: new Date(new Date().getTime() + task.start), end: new Date(new Date().getTime() + task.end), users: selectedUser || [] }])

                        }
                        iteration++


                    })
                }
            });

            toast(`Szablon odtworzony`, "success")
        } catch (err) {
            console.error(err);
        }

    };

    const [items, setItems] = useState([{ label: "Ładowanie" }])

    useEffect(() => {
        if (views) {
            let itemsB = [];
            if (views.length > 0) {
                views.forEach((view, i = 0) => {

                    itemsB[i] = {
                        label: view.name,
                        items: [
                            {
                                label: "Dodaj projekt",
                                icon: "pi pi-plus",
                                command: () => { addView(view._id) }
                            },
                            {
                                label: "Usuń projekt",
                                icon: "pi pi-trash",
                                command: () => { handleDeleteView(view._id, view.name) }
                            }
                        ]
                    }


                })
            }
            else {
                itemsB = [{ label: "Brak szablonów" }]
            }
            setItems(itemsB)


        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [views])


    const CalendarViews = [
        { name: 'Dzień', code: ViewMode.Day },
        { name: 'Tydzień', code: ViewMode.Week },
        { name: 'Miesiąc', code: ViewMode.Month },
        { name: 'Kwartał', code: ViewMode.QuarterYear },
        { name: 'Rok', code: ViewMode.Year }
    ];

    const sortOptions = [
        { name: 'Koordynator', code: 'coordinator' },
        { name: 'Nazwa projektu', code: 'projectName' },
        { name: 'Data początkowa', code: 'startDate' },
        { name: 'Wykonawcy', code: 'users' },
        { name: 'Branża', code: 'branch' }
    ];

    const projectStatusOptions = [
        { label: 'Bieżące', value: 'active' },
        { label: 'Archiwum', value: 'closed' },
        { label: 'Oferty', value: 'open' }
    ];

    const severityColors = {
        success: 'rgba(0, 255, 0, 0.5)',
        error: 'rgba(255, 32, 0, 0.5)',
        warning: 'rgba(255, 255, 0, 0.5)',
        info: 'rgba(0, 128, 255, 0.5)'
    };


    const getNextProjectDisplayOrder = () => {
        const projects = tasks.filter(t => t.type === 'project');
        const maxOrder = projects.length > 0
            ? Math.max(...projects.map(t => t.displayOrder || 0))
            : 0;
        return maxOrder + 1;
    };

    const handleAddGroup = () => {
        if (!project) return;
        
        // Nie pozwalaj na dodawanie projektów w archiwum
        if (selectedProjectStatus === 'closed') {
            toast('Nie można dodawać projektów w statusie "Archiwum"', 'warning');
            return;
        }
        
        const draftProject = {
            name: '',
            type: 'project',
            start: new Date(),
            end: new Date(new Date().getTime() + 24 * 60 * 60 * 1000),
            displayOrder: getNextProjectDisplayOrder(),
            description: '',
            progress: 0,
            status: selectedProjectStatus || 'active'
        };
        
        if (onCreateProjectRequest) {
            onCreateProjectRequest(draftProject);
        }
    };

    const handleChangeEditorMode = (e) => {

        setValue(e.value);
        e.value === "Tak" ? onChangeEditorMode(true) : onChangeEditorMode(false);

    }

    const sortPanelFooterTemplate = () => (
        <div className="flex align-items-center gap-2" style={{ padding: '0.75rem 1rem', borderTop: '1px solid var(--surface-border)' }}>
            <Checkbox
                inputId="show-projects-in-dropdown"
                checked={showProjects}
                onChange={(e) => onShowProjectsChange?.(e.checked)}
            />
            <label htmlFor="show-projects-in-dropdown" style={{ whiteSpace: 'nowrap', cursor: 'pointer' }}>
                Pokaż projekty
            </label>
        </div>
    );

    const start = (
        <div className="flex align-items-stretch flex-wrap gap-4" style={{ minHeight: "70px" }}>
            {(user?.type === "admin" || user?.type === "manager") && (
                <div className="flex align-items-center">
                    <UserDropdown
                        selectedUser={selectedUser}
                        onUserSelect={onChangeSelectedUser}
                        usersData={users}
                        institutionsData={institutions}
                        optionLabel="label"
                        className="w-15rem"
                        showBranchColor={true}
                        branchFilter={selectedBranch}
                    />
                </div>
            )}


            <div className="flex align-items-center">
                <BranchDropdown
                    selectedBranch={selectedBranch}
                    onBranchSelect={onChangeSelectedBranch}
                    className="w-full"
                    multiSelect={true}
                />
            </div>

            <div className="flex align-items-center w-64">
                <ProjectDropdown
                    selectedProject={project}
                    onProjectSelect={onChangeProject}
                    projects={tasks.filter(t => t.type === 'project')}
                    optionLabel="label"
                    className="w-20rem"
                />
            </div>

            <div className="flex align-items-center">
                <FloatLabel>
                    <Dropdown
                        id="project-status"
                        value={selectedProjectStatus || 'active'}
                        onChange={(e) => onChangeProjectStatus?.(e.value)}
                        options={projectStatusOptions}
                        placeholder="Status"
                        className="w-full md:w-12rem"
                    />
                    <label htmlFor="project-status">Status</label>
                </FloatLabel>
            </div>
        </div>
    );

    const end = (
        <div className="flex items-center gap-2">
            {(user?.type === "admin" || user?.type === "manager") && (
                <Button
                    icon="pi pi-undo"
                    className="p-button-sm p-button-warning"
                    onClick={handleUndo}
                    loading={isUndoing}
                    disabled={isUndoing}
                    tooltip={isUndoing ? 'Cofanie...' : 'Cofnij ostatnią operację (max 5 min)'}
                    tooltipOptions={{ position: 'bottom' }}
                />
            )}

            {(user?.type === "admin" || user?.type === "manager") && (
                <div className="flex align-items-center">
                    <SplitButton
                        icon="pi pi-plus"
                        className="p-button-sm p-button-success"
                        onClick={handleAddGroup}
                        model={items}
                        disabled={selectedProjectStatus === 'closed'}
                        tooltip={selectedProjectStatus === 'closed' ? 'Nie można dodawać projektów w archiwum' : 'Dodaj projekt'}
                        tooltipOptions={{ position: 'bottom' }}
                        visible={(user && (user.type === "admin" || user.type === "manager") ? "true" : "false")} />
                </div>
            )}

            <div className="flex align-items-center">
                <Menu model={expandCollapseMenuItems} popup ref={menuRef} />
                <Button
                    icon="pi pi-bars"
                    className="p-button-sm"
                    onClick={(e) => menuRef.current?.toggle(e)}
                    tooltip="Rozwiń/Zwiń"
                    tooltipOptions={{ position: 'bottom' }}
                />
            </div>

            <div className="flex align-items-center">
                <Button
                    icon="pi pi-cog"
                    className="p-button-sm"
                    onClick={() => setSettingsDialogVisible(true)}
                    tooltip="Ustawienia"
                    tooltipOptions={{ position: 'bottom' }}
                />
            </div>

            <FloatLabel>
                <Dropdown
                    id="sort-by"
                    value={sortBy}
                    onChange={(e) => onSortChange?.(e.value)}
                    options={sortOptions}
                    optionLabel="name"
                    placeholder="Sortowanie"
                    className="w-full md:w-13rem"
                    optionValue="code"
                    panelFooterTemplate={sortPanelFooterTemplate}
                />
                <label htmlFor="sort-by">Sortowanie</label>
            </FloatLabel>
            <FloatLabel>
                <Dropdown
                    id="view-mode"
                    value={view}
                    onChange={(e) => onViewModeChange(e.value)}
                    options={CalendarViews}
                    optionLabel="name"
                    placeholder="Widok"
                    className="w-full md:w-9rem"
                    optionValue="code"
                    visible={(!taskMenu ? "true" : "false")}
                />
                <label htmlFor="view-mode">Widok</label>
            </FloatLabel>

            {(user?.type === "admin" || user?.type === "manager") && (
                <Button
                    icon={value === 'Tak' ? 'pi pi-lock-open' : 'pi pi-lock'}
                    className="p-button-sm"
                    severity={value === 'Tak' ? 'success' : 'secondary'}
                    onClick={() => {
                        const newValue = value === 'Tak' ? 'Nie' : 'Tak';
                        setValue(newValue);
                        onChangeEditorMode(newValue === 'Tak');
                    }}
                    tooltip={value === 'Tak' ? 'Tryb edycji włączony' : 'Tryb edycji wyłączony'}
                    tooltipOptions={{ position: 'bottom' }}
                />
            )}

            <Button
                icon="pi pi-book"
                className="p-button-sm"
                onClick={() => setSidebarVisible(!sidebarVisible)}
                tooltip="Historia"
                tooltipOptions={{ position: 'bottom' }}
            />

            <Sidebar visible={sidebarVisible} position="right" onHide={() => setSidebarVisible(false)}>
                <div onScroll={handleToastSidebarScroll} style={{ height: '100%', overflowY: 'auto', paddingRight: '0.25rem' }}>
                    {toastHistory.map((toastItem) => (
                        <Card key={toastItem._id} className="m-1" title={toastItem.author} style={{ backgroundColor: severityColors[toastItem.severity] || 'rgba(200, 200, 200, 0.3)' }}>
                            <p className="m-0">
                                {toastItem.message}
                            </p>
                        </Card>
                    ))}

                    {isLoadingToasts && (
                        <div className="p-3 text-center">Ładowanie...</div>
                    )}

                    {!isLoadingToasts && toastHistory.length === 0 && (
                        <div className="p-3 text-center">Brak historii</div>
                    )}

                    {!toastHasMore && toastHistory.length > 0 && (
                        <div className="p-3 text-center text-500">To już wszystkie wiadomości</div>
                    )}
                </div>
            </Sidebar>
        </div>
    );

    return (
        <div className="card">
            <Toolbar start={start} end={end} />

            <Dialog
                header="Ustawienia wyświetlania"
                visible={settingsDialogVisible}
                style={{ width: '450px' }}
                onHide={() => setSettingsDialogVisible(false)}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button label="Anuluj" icon="pi pi-times" onClick={() => setSettingsDialogVisible(false)} className="p-button-text" />
                        <Button label="Zapisz" icon="pi pi-check" onClick={handleSaveSettings} autoFocus />
                    </div>
                }
            >
                <div className="flex flex-column gap-4">
                    <div className="flex flex-column gap-2">
                        <label htmlFor="rowHeight">Wysokość paska zadania: {tempRowHeight}px</label>
                        <Slider
                            id="rowHeight"
                            value={tempRowHeight}
                            onChange={(e) => setTempRowHeight(e.value)}
                            min={20}
                            max={100}
                            step={5}
                        />
                    </div>
                    <div className="flex flex-column gap-2">
                        <label htmlFor="columnWidth">Szerokość kolumny czasowej: {tempColumnWidth}px</label>
                        <Slider
                            id="columnWidth"
                            value={tempColumnWidth}
                            onChange={(e) => setTempColumnWidth(e.value)}
                            min={30}
                            max={200}
                            step={5}
                        />
                    </div>
                    <div className="flex flex-column gap-2">
                        <label htmlFor="barFill">Wypełnienie paska (% wysokości wiersza): {tempBarFill}%</label>
                        <Slider
                            id="barFill"
                            value={tempBarFill}
                            onChange={(e) => setTempBarFill(e.value)}
                            min={40}
                            max={100}
                            step={5}
                        />
                    </div>
                    <div className="flex flex-column gap-2">
                        <label style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Widoczność kolumn:</label>
                        <div className="flex flex-column gap-2" style={{ paddingLeft: '1rem' }}>
                            <div className="flex align-items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="col-coordinator"
                                    checked={tempColumnVisibility.coordinator}
                                    onChange={(e) => setTempColumnVisibility({ ...tempColumnVisibility, coordinator: e.target.checked })}
                                />
                                <label htmlFor="col-coordinator" style={{ cursor: 'pointer' }}>Koordynator</label>
                            </div>
                            <div className="flex align-items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="col-executor"
                                    checked={tempColumnVisibility.executor}
                                    onChange={(e) => setTempColumnVisibility({ ...tempColumnVisibility, executor: e.target.checked })}
                                />
                                <label htmlFor="col-executor" style={{ cursor: 'pointer' }}>Wykonawca</label>
                            </div>
                            <div className="flex align-items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="col-branch"
                                    checked={tempColumnVisibility.branch}
                                    onChange={(e) => setTempColumnVisibility({ ...tempColumnVisibility, branch: e.target.checked })}
                                />
                                <label htmlFor="col-branch" style={{ cursor: 'pointer' }}>Branża</label>
                            </div>
                            <div className="flex align-items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="col-name"
                                    checked={tempColumnVisibility.name}
                                    onChange={(e) => setTempColumnVisibility({ ...tempColumnVisibility, name: e.target.checked })}
                                />
                                <label htmlFor="col-name" style={{ cursor: 'pointer' }}>Nazwa</label>
                            </div>
                            <div className="flex align-items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="col-start"
                                    checked={tempColumnVisibility.start}
                                    onChange={(e) => setTempColumnVisibility({ ...tempColumnVisibility, start: e.target.checked })}
                                />
                                <label htmlFor="col-start" style={{ cursor: 'pointer' }}>Początek</label>
                            </div>
                            <div className="flex align-items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="col-end"
                                    checked={tempColumnVisibility.end}
                                    onChange={(e) => setTempColumnVisibility({ ...tempColumnVisibility, end: e.target.checked })}
                                />
                                <label htmlFor="col-end" style={{ cursor: 'pointer' }}>Koniec</label>
                            </div>
                            <div className="flex align-items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="col-progress"
                                    checked={tempColumnVisibility.progress}
                                    onChange={(e) => setTempColumnVisibility({ ...tempColumnVisibility, progress: e.target.checked })}
                                />
                                <label htmlFor="col-progress" style={{ cursor: 'pointer' }}>Postęp</label>
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
        </div>
    );
}